import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Link, 
  List, 
  ListItem, 
  ListItemButton,
  Breadcrumbs,
  Paper,
  Button
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

const Privacy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('overview');

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'overview',
        'information-we-collect',
        'how-we-use',
        'legal-basis',
        'how-we-share',
        'data-storage',
        'data-retention',
        'your-rights',
        'children-privacy',
        'international-transfers',
        'third-party-links',
        'data-breach',
        'changes',
        'contact'
      ];

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for any fixed headers
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const navItems = [
    { id: 'overview', label: 'Privacy policy overview' },
    { id: 'information-we-collect', label: 'Information we collect' },
    { id: 'how-we-use', label: 'How we use personal information' },
    { id: 'legal-basis', label: 'Legal basis for processing' },
    { id: 'how-we-share', label: 'How we share information' },
    { id: 'data-storage', label: 'Data storage & security' },
    { id: 'data-retention', label: 'Data retention' },
    { id: 'your-rights', label: 'Your rights' },
    { id: 'children-privacy', label: "Children's privacy" },
    { id: 'international-transfers', label: 'International transfers' },
    { id: 'third-party-links', label: 'Third-party links' },
    { id: 'data-breach', label: 'Data breach notifications' },
    { id: 'changes', label: 'Changes to this policy' },
    { id: 'contact', label: 'Contact us' }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button and Breadcrumb */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          variant="outlined"
          sx={{ mb: { xs: 1, sm: 0 } }}
        >
          Back to Home
        </Button>
        <Breadcrumbs aria-label="breadcrumb">
          <Link 
            color="inherit" 
            component="button"
            onClick={() => navigate('/')}
            underline="hover"
            sx={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}
          >
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>
      </Box>

      <Grid container spacing={4}>
        {/* Left Navigation */}
        <Grid item xs={12} md={3}>
          <Paper 
            elevation={0} 
            sx={{ 
              position: 'sticky', 
              top: 100,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Privacy Policy
            </Typography>
            <List sx={{ p: 0 }}>
              {navItems.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => scrollToSection(item.id)}
                    selected={activeSection === item.id}
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        fontWeight: 600,
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        color: activeSection === item.id ? 'primary.main' : 'text.primary'
                      }}
                    >
                      {item.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          <Box sx={{ maxWidth: '800px' }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 4, color: 'text.secondary' }}>
              Effective Date: 01/12/2025
            </Typography>

            {/* Overview */}
            <Box id="overview" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                1. Overview
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Website & Platform:</strong> https://workflow.estate
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Company:</strong> CodePulse Pty Ltd ("we", "us", "our")
              </Typography>
              <Typography variant="body1" paragraph>
                Workflow.Estate ("WE") is an end-to-end real estate workflow management platform that helps real-estate agents manage property lifecycle workflows, vendor interactions, open-home attendees, buyer enquiries, contractors, and related tasks.
              </Typography>
              <Typography variant="body1" paragraph>
                This Privacy Policy explains how we collect, use, store, disclose, and protect personal information when you use our platform, mobile applications, and associated services.
              </Typography>
              <Typography variant="body1" paragraph>
                We comply with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth) and adopt concepts from GDPR for broader transparency.
              </Typography>
              <Typography variant="body1" paragraph>
                By using Workflow.Estate, you agree to this Privacy Policy.
              </Typography>
            </Box>

            {/* Information We Collect */}
            <Box id="information-we-collect" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                2. Information We Collect
              </Typography>
              <Typography variant="body1" paragraph>
                We collect the following categories of personal information:
              </Typography>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                2.1 Information You Provide
              </Typography>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Account Information
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Name</Typography></li>
                <li><Typography variant="body1" component="span">Email address</Typography></li>
                <li><Typography variant="body1" component="span">Phone number</Typography></li>
                <li><Typography variant="body1" component="span">Company/agency name</Typography></li>
                <li><Typography variant="body1" component="span">Role (e.g., Agent, Admin)</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Property & Workflow Data
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Vendor contact information</Typography></li>
                <li><Typography variant="body1" component="span">Buyer information collected during enquiries or inspections</Typography></li>
                <li><Typography variant="body1" component="span">Open-home attendee details</Typography></li>
                <li><Typography variant="body1" component="span">Notes, tasks, comments, workflow records</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Contractor Portal Information
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Contractor names</Typography></li>
                <li><Typography variant="body1" component="span">Contact details</Typography></li>
                <li><Typography variant="body1" component="span">Job statuses and updates</Typography></li>
                <li><Typography variant="body1" component="span">Uploaded documents or media</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Payment & Billing (If applicable)
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Billing name</Typography></li>
                <li><Typography variant="body1" component="span">Billing email</Typography></li>
                <li><Typography variant="body1" component="span">Payment details processed through third-party providers (e.g., Stripe)</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Support Requests
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Messages, attachments, screenshots, or logs sent to WE support</Typography></li>
              </Box>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                2.2 Information Collected Automatically
              </Typography>
              <Typography variant="body1" paragraph>
                When accessing our platform:
              </Typography>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                System & Usage Data
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">IP address</Typography></li>
                <li><Typography variant="body1" component="span">Browser type</Typography></li>
                <li><Typography variant="body1" component="span">Device information</Typography></li>
                <li><Typography variant="body1" component="span">Pages visited</Typography></li>
                <li><Typography variant="body1" component="span">Actions taken in the CRM</Typography></li>
                <li><Typography variant="body1" component="span">Login timestamps</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Cookies & Tracking Technologies
              </Typography>
              <Typography variant="body1" paragraph>
                We use cookies for:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Authentication</Typography></li>
                <li><Typography variant="body1" component="span">Session management</Typography></li>
                <li><Typography variant="body1" component="span">Analytics</Typography></li>
                <li><Typography variant="body1" component="span">Platform performance</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                You may disable cookies via your browser, but some features may not work.
              </Typography>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                2.3 Information From Third Parties
              </Typography>
              <Typography variant="body1" paragraph>
                We may receive information from:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Real estate agencies onboarding users</Typography></li>
                <li><Typography variant="body1" component="span">Contractors invited to the portal</Typography></li>
                <li><Typography variant="body1" component="span">Payment processors (e.g., Stripe)</Typography></li>
                <li><Typography variant="body1" component="span">Email providers (e.g., SendGrid delivery confirmations)</Typography></li>
                <li><Typography variant="body1" component="span">Property data integrations (if enabled later)</Typography></li>
              </Box>
            </Box>

            {/* How We Use */}
            <Box id="how-we-use" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                3. How We Use Personal Information
              </Typography>
              <Typography variant="body1" paragraph>
                We use collected information for the following purposes:
              </Typography>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Platform Operation
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Provide CRM workflow features</Typography></li>
                <li><Typography variant="body1" component="span">Manage end-to-end property workflows</Typography></li>
                <li><Typography variant="body1" component="span">Record open-home attendees</Typography></li>
                <li><Typography variant="body1" component="span">Facilitate communication with buyers, vendors, contractors</Typography></li>
                <li><Typography variant="body1" component="span">Manage contractor tasks and portals</Typography></li>
                <li><Typography variant="body1" component="span">Enable secure login and authentication</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Service Improvement
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Analyse usage for performance optimisation</Typography></li>
                <li><Typography variant="body1" component="span">Develop new features and platform enhancements</Typography></li>
                <li><Typography variant="body1" component="span">Train internal systems (non-identifiable data only)</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Communications
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Transactional notifications</Typography></li>
                <li><Typography variant="body1" component="span">Workflow updates</Typography></li>
                <li><Typography variant="body1" component="span">Account reminders</Typography></li>
                <li><Typography variant="body1" component="span">Service announcements</Typography></li>
                <li><Typography variant="body1" component="span">Support responses</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Security & Compliance
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Prevent unauthorised access</Typography></li>
                <li><Typography variant="body1" component="span">Detect fraud or misuse</Typography></li>
                <li><Typography variant="body1" component="span">Meet legal obligations</Typography></li>
              </Box>

              <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Marketing (Optional & Consent-Based)
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Announcements of new features</Typography></li>
                <li><Typography variant="body1" component="span">Educational materials</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                You can opt out anytime.
              </Typography>
            </Box>

            {/* Legal Basis */}
            <Box id="legal-basis" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                4. Legal Basis for Processing (GDPR Concepts)
              </Typography>
              <Typography variant="body1" paragraph>
                Where applicable, we process personal data based on:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Consent (e.g., marketing communications)</Typography></li>
                <li><Typography variant="body1" component="span">Contractual necessity (to provide the CRM)</Typography></li>
                <li><Typography variant="body1" component="span">Legitimate interest (platform improvement, fraud prevention)</Typography></li>
                <li><Typography variant="body1" component="span">Legal obligation (e.g., regulatory compliance)</Typography></li>
              </Box>
            </Box>

            {/* How We Share */}
            <Box id="how-we-share" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                5. How We Share Information
              </Typography>
              <Typography variant="body1" paragraph>
                We do not sell personal information.
              </Typography>
              <Typography variant="body1" paragraph>
                We may share data with:
              </Typography>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                5.1 Service Providers
              </Typography>
              <Typography variant="body1" paragraph>
                Trusted partners that help us deliver the service:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Cloud hosting providers (e.g., OCI)</Typography></li>
                <li><Typography variant="body1" component="span">Email/sms providers (e.g., SendGrid)</Typography></li>
                <li><Typography variant="body1" component="span">Payment processors (e.g., Stripe)</Typography></li>
                <li><Typography variant="body1" component="span">Analytics tools</Typography></li>
                <li><Typography variant="body1" component="span">Support & ticketing systems</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                All providers are bound by confidentiality agreements.
              </Typography>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                5.2 Within Your Agency
              </Typography>
              <Typography variant="body1" paragraph>
                Agency admins may view:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Loaned workflows</Typography></li>
                <li><Typography variant="body1" component="span">Attendee logs</Typography></li>
                <li><Typography variant="body1" component="span">Tasks</Typography></li>
                <li><Typography variant="body1" component="span">Property files</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                Access is controlled by your agency's internal permissions.
              </Typography>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                5.3 Contractors
              </Typography>
              <Typography variant="body1" paragraph>
                When you assign a job to a contractor:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Only relevant property/job information is shared with them</Typography></li>
              </Box>

              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                5.4 Legal Requirements
              </Typography>
              <Typography variant="body1" paragraph>
                We may disclose data if required by:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Subpoenas</Typography></li>
                <li><Typography variant="body1" component="span">Court orders</Typography></li>
                <li><Typography variant="body1" component="span">Regulatory obligations</Typography></li>
                <li><Typography variant="body1" component="span">Investigations into fraud or misuse</Typography></li>
              </Box>
            </Box>

            {/* Data Storage */}
            <Box id="data-storage" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                6. Data Storage & Security
              </Typography>
              <Typography variant="body1" paragraph>
                We implement multiple layers of security including:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Encryption in transit (HTTPS/TLS)</Typography></li>
                <li><Typography variant="body1" component="span">Encryption at rest</Typography></li>
                <li><Typography variant="body1" component="span">Strict access controls</Typography></li>
                <li><Typography variant="body1" component="span">Audit logging</Typography></li>
                <li><Typography variant="body1" component="span">Authentication protections</Typography></li>
                <li><Typography variant="body1" component="span">Periodic security reviews</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                Data is primarily stored in Australia (Sydney region) unless otherwise specified.
              </Typography>
            </Box>

            {/* Data Retention */}
            <Box id="data-retention" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                7. Data Retention
              </Typography>
              <Typography variant="body1" paragraph>
                We retain information for as long as necessary to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Provide services</Typography></li>
                <li><Typography variant="body1" component="span">Comply with legal obligations</Typography></li>
                <li><Typography variant="body1" component="span">Resolve disputes</Typography></li>
                <li><Typography variant="body1" component="span">Enforce agreements</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                Agencies may request deletion of agency-owned data at any time.
              </Typography>
            </Box>

            {/* Your Rights */}
            <Box id="your-rights" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                8. Your Rights
              </Typography>
              <Typography variant="body1" paragraph>
                Depending on your jurisdiction, you may request:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Access to your data</Typography></li>
                <li><Typography variant="body1" component="span">Correction of inaccurate data</Typography></li>
                <li><Typography variant="body1" component="span">Data export (where feasible)</Typography></li>
                <li><Typography variant="body1" component="span">Deletion of your personal information</Typography></li>
                <li><Typography variant="body1" component="span">Restriction of processing</Typography></li>
                <li><Typography variant="body1" component="span">Withdrawal of marketing consent</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                To exercise these rights, contact: <Link href="mailto:support@workflow.estate">support@workflow.estate</Link>
              </Typography>
              <Typography variant="body1" paragraph>
                Requests may require identity verification.
              </Typography>
            </Box>

            {/* Children's Privacy */}
            <Box id="children-privacy" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                9. Children's Privacy
              </Typography>
              <Typography variant="body1" paragraph>
                Workflow.Estate is not designed for individuals under 16.
              </Typography>
              <Typography variant="body1" paragraph>
                We do not knowingly collect data from minors unless provided by an agency in connection with real-estate processes (e.g., household information).
              </Typography>
              <Typography variant="body1" paragraph>
                Agencies are responsible for obtaining required consents.
              </Typography>
            </Box>

            {/* International Transfers */}
            <Box id="international-transfers" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                10. International Transfers
              </Typography>
              <Typography variant="body1" paragraph>
                If data is transferred outside Australia (e.g., cloud networks), we ensure:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Adequate protection</Typography></li>
                <li><Typography variant="body1" component="span">Contractual safeguards</Typography></li>
                <li><Typography variant="body1" component="span">Compliance with applicable laws</Typography></li>
              </Box>
            </Box>

            {/* Third-Party Links */}
            <Box id="third-party-links" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                11. Third-Party Links
              </Typography>
              <Typography variant="body1" paragraph>
                Our platform may link to external sites (e.g., external property website).
              </Typography>
              <Typography variant="body1" paragraph>
                We are not responsible for their privacy practices.
              </Typography>
            </Box>

            {/* Data Breach */}
            <Box id="data-breach" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                12. Data Breach Notifications
              </Typography>
              <Typography variant="body1" paragraph>
                If a data breach affecting personal information occurs, we will:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Assess the severity</Typography></li>
                <li><Typography variant="body1" component="span">Notify affected users where required</Typography></li>
                <li><Typography variant="body1" component="span">Notify OAIC (Australia) where legally applicable</Typography></li>
                <li><Typography variant="body1" component="span">Take immediate corrective actions</Typography></li>
              </Box>
            </Box>

            {/* Changes */}
            <Box id="changes" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                13. Changes to This Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We may update this Privacy Policy when necessary.
              </Typography>
              <Typography variant="body1" paragraph>
                We will notify users of significant changes via:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><Typography variant="body1" component="span">Email</Typography></li>
                <li><Typography variant="body1" component="span">In-app notifications</Typography></li>
                <li><Typography variant="body1" component="span">Updated "Effective Date"</Typography></li>
              </Box>
            </Box>

            {/* Contact */}
            <Box id="contact" sx={{ mb: 6, scrollMarginTop: '100px' }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                14. Contact Us
              </Typography>
              <Typography variant="body1" paragraph>
                For privacy questions, complaints, or requests:
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                <Typography variant="body1" paragraph sx={{ mb: 0.5 }}>
                  <strong>CodePulse Pty Ltd</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 0.5 }}>
                  Email: <Link href="mailto:support@workflow.estate">support@workflow.estate</Link>
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 0.5 }}>
                  Website: <Link href="https://workflow.estate" target="_blank" rel="noopener noreferrer">https://workflow.estate</Link>
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                If you remain unsatisfied, you may contact the Office of the Australian Information Commissioner (OAIC).
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Privacy;
